import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const physicsUnit5Notes: UnitNotes = {
  unitId: "unit-5",
  unitName: "Unit 5: Torque and Rotational Dynamics",
  examWeight: "10–15%",
  focus: "Shifting from point-mass models to rigid bodies: understanding how forces cause rotation and the resistance to that motion.",
  sections: [
    {
      id: "topic-5-1",
      title: "Topic 5.1: Rotational Kinematics",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔄 The Rigid Body Model</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A <strong>rigid system</strong> is an object where the distance between any two points remains constant. While linear motion treats objects as dots, rotational motion considers the physical size and shape of objects.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📐 Angular Quantities</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Angular Position (θ)" 
                definition="The angle through which a point has rotated, measured in radians. (1 rev = 2π rad)."
              />
              <KeyTerm 
                term="Angular Velocity (ω)" 
                definition="The rate of change of angular position."
              />
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <BlockMath math="\omega_{avg} = \frac{\Delta\theta}{\Delta t}" />
              </div>
              <KeyTerm 
                term="Angular Acceleration (α)" 
                definition="The rate of change of angular velocity."
              />
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <BlockMath math="\alpha_{avg} = \frac{\Delta\omega}{\Delta t}" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📝 Rotational Kinematic Equations</h3>
            <p className="text-sm text-slate-600 mb-4 italic">Applied only when angular acceleration (α) is constant.</p>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-slate-200 rounded-lg">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="px-4 py-2 text-left text-slate-800 border-b">Linear (Constant a)</th>
                    <th className="px-4 py-2 text-left text-slate-800 border-b">Rotational (Constant α)</th>
                  </tr>
                </thead>
                <tbody className="text-sm text-slate-700">
                  <tr>
                    <td className="px-4 py-3 border-b"><InlineMath math="v = v_0 + at" /></td>
                    <td className="px-4 py-3 border-b"><InlineMath math="\omega = \omega_0 + \alpha t" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 border-b"><InlineMath math="\Delta x = v_0 t + \frac{1}{2}at^2" /></td>
                    <td className="px-4 py-3 border-b"><InlineMath math="\Delta\theta = \omega_0 t + \frac{1}{2}\alpha t^2" /></td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3"><InlineMath math="v^2 = v_0^2 + 2a\Delta x" /></td>
                    <td className="px-4 py-3"><InlineMath math="\omega^2 = \omega_0^2 + 2\alpha\Delta\theta" /></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-2",
      title: "Topic 5.2: Connecting Linear and Rotational Motion",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌉 The Bridge Equations</h3>
            <p className="text-slate-700 mb-4">Every point on a rigid body shares the same <InlineMath math="\omega" /> and <InlineMath math="\alpha" />, but points at different distances (<InlineMath math="r" />) from the axis have different linear properties.</p>
            <div className="grid md:grid-cols-3 gap-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-center">
                <p className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-2">Arc Length</p>
                <BlockMath math="s = r\theta" />
              </div>
              <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 text-center">
                <p className="text-xs font-bold text-indigo-900 uppercase tracking-wider mb-2">Tangential Velocity</p>
                <BlockMath math="v_t = r\omega" />
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 text-center">
                <p className="text-xs font-bold text-purple-900 uppercase tracking-wider mb-2">Tangential Accel.</p>
                <BlockMath math="a_t = r\alpha" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚀 Tangential vs. Centripetal</h3>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">Tangential Accel (aₜ)</h4>
                <p className="text-sm text-slate-700 mt-1">Changes the <strong>magnitude</strong> of velocity (speeding up/slowing down). Acts tangent to the path.</p>
              </div>
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-4 rounded-lg border-l-4 border-cyan-400">
                <h4 className="font-bold text-cyan-900">Centripetal Accel (a꜀)</h4>
                <p className="text-sm text-slate-700 mt-1">Changes the <strong>direction</strong> of velocity. <InlineMath math="a_c = v^2/r = \omega^2 r" />. Acts toward the center.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-3",
      title: "Topic 5.3: Torque",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔧 What is Torque?</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Torque (<InlineMath math="\tau" />) is the rotational equivalent of force. It measures a force's ability to cause angular acceleration about a pivot.
            </p>
            <div className="bg-slate-800 text-white p-6 rounded-xl mb-4 text-center">
              <BlockMath math="\tau = r F \sin(\theta)" />
              <p className="text-xs text-slate-400 mt-2">Where θ is the angle between the Force and the lever arm.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📏 The Lever Arm (r⊥)</h3>
            <p className="text-slate-700 mb-4">The perpendicular distance from the axis of rotation to the line of action of the force.</p>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
              <BlockMath math="\tau = r_\perp F" />
              <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-1">
                <li><strong>Max Torque:</strong> Force applied at <InlineMath math="90^\circ" /> to the radius.</li>
                <li><strong>Zero Torque:</strong> Force applied parallel to the radius (pointing at the pivot).</li>
              </ul>
            </div>
          </section>

          <ImportantNote>
            <strong>Extended Force Diagrams:</strong> For torque, you cannot represent objects as dots. You must draw the forces originating exactly where they are applied to calculate the correct radius.
          </ImportantNote>
        </div>
      )
    },
    {
      id: "topic-5-4",
      title: "Topic 5.4: Rotational Inertia (Moment of Inertia)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧱 Resistance to Rotation</h3>
            <p className="text-slate-700 mb-4">Rotational Inertia (<InlineMath math="I" />) depends on mass and <strong>mass distribution</strong>. The further mass is from the axis, the higher the inertia.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                <h4 className="font-bold text-rose-900">Point Mass</h4>
                <BlockMath math="I = mr^2" />
              </div>
              <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                <h4 className="font-bold text-rose-900">System of Particles</h4>
                <BlockMath math="I_{tot} = \sum m_i r_i^2" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🍩 Common Rigid Shapes</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-slate-50 border rounded-lg">
                <span className="font-medium">Hoop (Mass at rim)</span>
                <InlineMath math="I = MR^2" />
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 border rounded-lg">
                <span className="font-medium">Solid Disk / Cylinder</span>
                <InlineMath math="I = \frac{1}{2}MR^2" />
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 border rounded-lg">
                <span className="font-medium">Solid Sphere</span>
                <InlineMath math="I = \frac{2}{5}MR^2" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📍 Parallel Axis Theorem</h3>
            <p className="text-sm text-slate-700 mb-2">Used to find inertia about an axis shifted by distance <InlineMath math="d" /> from the center of mass.</p>
            <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
              <BlockMath math="I = I_{cm} + Md^2" />
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-5",
      title: "Topic 5.5: Rotational Equilibrium",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚖️ Conditions for Static Equilibrium</h3>
            <p className="text-slate-700 mb-4">An object is truly "still" only if it has no translational or rotational acceleration.</p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="p-4 border-2 border-dashed border-slate-300 rounded-lg text-center">
                <p className="font-bold text-slate-800">Translational</p>
                <BlockMath math="\sum \vec{F} = 0" />
              </div>
              <div className="p-4 border-2 border-dashed border-slate-300 rounded-lg text-center">
                <p className="font-bold text-slate-800">Rotational</p>
                <BlockMath math="\sum \vec{\tau} = 0" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💡 Problem Solving Tip</h3>
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-sm text-slate-800">
                <strong>Choosing a Pivot:</strong> If an object is not rotating, you can pick <em>any</em> point as your pivot. 
                <strong>Strategy:</strong> Place the pivot at the location of an unknown force. This makes its <InlineMath math="r = 0" />, removing it from the torque equation!
              </p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-5-6",
      title: "Topic 5.6: Newton’s Second Law in Rotational Form",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚙️ The Dynamic Equation</h3>
            <p className="text-slate-700 mb-4">Net torque causes angular acceleration, limited by rotational inertia.</p>
            <div className="bg-blue-600 text-white p-6 rounded-xl text-center">
              <BlockMath math="\sum \tau_{net} = I \alpha" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⛓️ Connected Systems (Pulleys with Mass)</h3>
            <p className="text-slate-700 mb-4">In Unit 5, pulleys are no longer massless. This leads to two critical changes:</p>
            <ul className="list-disc pl-6 space-y-3 text-slate-700">
              <li><strong>Tension is NOT equal:</strong> <InlineMath math="T_1 \neq T_2" /> because a net torque is required to make the pulley itself rotate.</li>
              <li><strong>Coupled Motion:</strong> The linear acceleration of the block is linked to the pulley's angular acceleration via <InlineMath math="a = R\alpha" />.</li>
            </ul>
          </section>

          <ImportantNote>
            <strong>Rolling Races:</strong> In a race down a ramp, the object with the <strong>lowest</strong> rotational inertia (mass concentrated at center) wins because it wastes less energy on rotation and keeps more for translation. <em>Sphere (2/5) wins over Hoop (1).</em>
          </ImportantNote>
        </div>
      )
    }
  ]
};

export default physicsUnit5Notes;